import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {IDailyConsumptionDto, IUnitDto} from '../../../api/api';
import {
  getFirstDayInPreviousMonth,
  getFirstDayInPreviousQuarter,
  getLastDayInPreviousMonth,
  getLastDayInPreviousQuarter,
  getMondayInPreviousWeek,
  getSundayInPreviousWeek,
  getYesterday,
} from '../../../utilities/helpers/date-helpers';
import {countNumberOfDaysBetweenDates} from '../../../utilities/platform-helpers/date-helpers';
import {
  countUniqueConsumptionTimeStamps,
  getConnectionIdsInPeriod,
  getOneYearPriorSortingDays,
  setMonthlyConsumption,
} from './energy-consumption-helper';
import {
  getConsumption,
  getConsumptionByDate,
  getConsumptionByDateOneYearPrior,
  getConsumptionOneYearPrior,
  getUnits,
} from './operation';
import {EnergyConsumptionState, FixedPeriodCategory, SortingDays} from './types';

const initialState: EnergyConsumptionState = {
  consumption: [] as IDailyConsumptionDto[],
  consumptionOneYearPrior: [] as IDailyConsumptionDto[],
  units: [] as IUnitDto[],
  pending: false,
  isSuccess: false,
  totalConsumption: 0,
  sortingDays: {
    fromDate: getFirstDayInPreviousMonth(),
    toDate: getLastDayInPreviousMonth(),
  },
  numberOfDays: 0,
  selectedMeasuringPointIds: [] as string[],
  idsInPeriod: [] as string[],
  idsInPeriodOneYearPrior: [] as string[],
  monthlyConsumption: 0,
  currentFixedPeriodCategory: FixedPeriodCategory.Month,
  priorFixedPeriodCategory: FixedPeriodCategory.Month,
  periodAverage: 0,
  yearPriorSelected: false,
};

const handleSelectedUnits = (state: EnergyConsumptionState) => {
  state.numberOfDays = countNumberOfDaysBetweenDates(state.sortingDays.fromDate, state.sortingDays.toDate);
  let idsInPeriod = getConnectionIdsInPeriod(state.units, state.sortingDays);

  // Find the IDs that are in selectedMeasuringPointIds but no longer in idsInPeriod
  state.selectedMeasuringPointIds = state.selectedMeasuringPointIds.filter((id) => idsInPeriod.includes(id));
  state.idsInPeriod = idsInPeriod;

  if (state.yearPriorSelected) {
    let oneYearPriorSortingDays = getOneYearPriorSortingDays(state.sortingDays);
    let idsInPeriodOneYearPrior = getConnectionIdsInPeriod(state.units, oneYearPriorSortingDays);
    
    // Find the selectedMeasuringPointIds that are in either idsInPeriodOneYearPrior or idsInPeriod
    state.selectedMeasuringPointIds = state.selectedMeasuringPointIds.filter(
      (id) => idsInPeriodOneYearPrior.includes(id) || idsInPeriod.includes(id)
    );
    state.idsInPeriodOneYearPrior = idsInPeriodOneYearPrior;
  }
};

export const energyConsumptionSlice = createSlice({
  name: 'energyConsumption',
  initialState,
  reducers: {
    setTotalConsumption: (state, action: PayloadAction<number>) => {
      if (!action.payload) {
        state.totalConsumption = 0;
        state.periodAverage = 0;
        return;
      }

      state.totalConsumption = action.payload;
      let filteredConsumption = state.consumption.filter(
        (i) => i.connectionId && state.selectedMeasuringPointIds.includes(i.connectionId),
      );
      state.periodAverage = action.payload / countUniqueConsumptionTimeStamps(filteredConsumption);
    },

    setSortingDays: (state, action: PayloadAction<SortingDays>) => {
      if (action.payload) {
        state.sortingDays.fromDate = action.payload.fromDate;
        state.sortingDays.toDate = action.payload.toDate;
        state.numberOfDays = countNumberOfDaysBetweenDates(action.payload.fromDate, action.payload.toDate);

        if (state.numberOfDays === 1) {
          state.currentFixedPeriodCategory = FixedPeriodCategory.Day;
        } else if (state.numberOfDays === 7) {
          state.currentFixedPeriodCategory = FixedPeriodCategory.Week;

          // Possible month length
        } else if (state.numberOfDays >= 28 && state.numberOfDays <= 31) {
          state.currentFixedPeriodCategory = FixedPeriodCategory.Month;

          // Possible quarter length
        } else if (state.numberOfDays >= 89 && state.numberOfDays <= 92) {
          state.currentFixedPeriodCategory = FixedPeriodCategory.Quarter;
        } else {
          state.currentFixedPeriodCategory = FixedPeriodCategory.NotFixed;
        }

        handleSelectedUnits(state);
      }
    },

    setNumberOfDays: (state, action: PayloadAction<SortingDays>) => {
      if (action.payload) {
        state.numberOfDays = countNumberOfDaysBetweenDates(state.sortingDays.fromDate, state.sortingDays.toDate);
      }
    },

    setSelectedMeasuringPointIds: (state, action: PayloadAction<string[]>) => {
      if (action.payload) {
        state.selectedMeasuringPointIds = action.payload;
      }
    },
    setMeasuringPointIdsInPeriod: (state, action: PayloadAction<string[]>) => {
      if (action.payload) {
        state.idsInPeriod = action.payload;
      }
    },

    setPeriodCategory: (state, action: PayloadAction<FixedPeriodCategory>) => {
      if (action.payload !== undefined) {
        state.currentFixedPeriodCategory = action.payload;

        switch (action.payload) {
          case FixedPeriodCategory.Day:
            state.sortingDays.fromDate = getYesterday();
            state.sortingDays.toDate = getYesterday();
            break;

          case FixedPeriodCategory.Week:
            state.sortingDays.fromDate = getMondayInPreviousWeek();
            state.sortingDays.toDate = getSundayInPreviousWeek();
            break;

          case FixedPeriodCategory.Month:
            state.sortingDays.fromDate = getFirstDayInPreviousMonth();
            state.sortingDays.toDate = getLastDayInPreviousMonth();
            break;

          case FixedPeriodCategory.Quarter:
            state.sortingDays.fromDate = getFirstDayInPreviousQuarter();
            state.sortingDays.toDate = getLastDayInPreviousQuarter();
            break;

          default:
            state.sortingDays.fromDate = getFirstDayInPreviousMonth();
            state.sortingDays.toDate = getLastDayInPreviousMonth();
            break;
        }

        handleSelectedUnits(state);
      }
    },

    setYearPriorSelected: (state, action: PayloadAction<boolean>) => {
      state.yearPriorSelected = action.payload;
      handleSelectedUnits(state);

      if (action.payload === false) {
        // Clean up data from prior year
        state.idsInPeriodOneYearPrior = [];
        state.consumptionOneYearPrior = [];
      }
    },

    resetConsumptionOneYearPriorIds: (state) => {
      state.idsInPeriodOneYearPrior = initialState.idsInPeriodOneYearPrior;
    },
  },
  extraReducers: (builder) => {
    // Get DailyConsumptionDtos - DateRange
    builder.addCase(getConsumption.fulfilled, (state, action) => {
      if (action.payload) {
        state.pending = false;
        state.consumption = action.payload;
        state.monthlyConsumption = setMonthlyConsumption(action.payload);
      }
    });
    builder.addCase(getConsumption.pending, (state, action) => {
      state.pending = true;
    });

    builder.addCase(getConsumption.rejected, (state, action) => {
      state.pending = false;
    });

    // Get DailyConsumptionDtos - ByDate
    builder.addCase(getConsumptionByDate.fulfilled, (state, action) => {
      if (action.payload) {
        state.pending = false;
        state.consumption = action.payload;
      }
    });
    builder.addCase(getConsumptionByDate.pending, (state, action) => {
      state.pending = true;
      if (state.currentFixedPeriodCategory !== state.priorFixedPeriodCategory) {
        state.consumption = initialState.consumption;
        state.consumptionOneYearPrior = initialState.consumptionOneYearPrior;
      }

      state.priorFixedPeriodCategory = state.currentFixedPeriodCategory;
    });

    builder.addCase(getConsumptionByDate.rejected, (state, action) => {
      state.pending = false;
    });

    // Get One Year Prior DailyConsumptionDtos - DateRange
    builder.addCase(getConsumptionOneYearPrior.fulfilled, (state, action) => {
      if (action.payload) {
        state.pending = false;
        state.consumptionOneYearPrior = action.payload;
      }
    });
    builder.addCase(getConsumptionOneYearPrior.pending, (state, action) => {
      state.pending = true;
      if (state.currentFixedPeriodCategory !== state.priorFixedPeriodCategory) {
        state.consumption = initialState.consumption;
        state.consumptionOneYearPrior = initialState.consumptionOneYearPrior;
      }

      state.priorFixedPeriodCategory = state.currentFixedPeriodCategory;
    });

    builder.addCase(getConsumptionOneYearPrior.rejected, (state, action) => {
      state.pending = false;
    });

    // Get One Year Prior DailyConsumptionDtos - ByDate
    builder.addCase(getConsumptionByDateOneYearPrior.fulfilled, (state, action) => {
      if (action.payload) {
        state.pending = false;
        state.consumptionOneYearPrior = action.payload;
      }
    });
    builder.addCase(getConsumptionByDateOneYearPrior.pending, (state, action) => {
      state.pending = true;
    });

    builder.addCase(getConsumptionByDateOneYearPrior.rejected, (state, action) => {
      state.pending = false;
    });

    // Get UnitDtos
    builder.addCase(getUnits.fulfilled, (state, action) => {
      if (action.payload) {
        state.units = action.payload;
        state.selectedMeasuringPointIds = state.units.map((unit) => unit.connectionid!);
        state.idsInPeriod = getConnectionIdsInPeriod(state.units, state.sortingDays);
      }
    });
  },
});

export const {
  setTotalConsumption,
  setSortingDays,
  setPeriodCategory,
  setNumberOfDays,
  setSelectedMeasuringPointIds,
  setYearPriorSelected,
  resetConsumptionOneYearPriorIds: resetConsumptionOneYearPrior,
} = energyConsumptionSlice.actions;
export default energyConsumptionSlice.reducer;
